import { useEffect, useMemo, useState } from "react";
import AxiosInstance from "../Home/AxiosInstance";
import { useNavigate, useParams } from "react-router-dom";
import {
  useTable,
  useGlobalFilter,
  useSortBy,
  useResizeColumns,
  usePagination,
} from "react-table";
import { FiEdit, FiTrash, FiArrowLeft } from "react-icons/fi";
import UserHeader from "./UserHeader";
import Footer from "../Home/Footer";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min";
import "./Enrollments.css";
import "./employerPortal.css";

export default function Enrollments() {
  const navigate = useNavigate();
  const { batchId } = useParams();

  const [enrollments, setEnrollments] = useState([]);
  const [reviews, setReviews] = useState([]);

  const [enrollmentFilter, setEnrollmentFilter] = useState("");
  const [reviewFilter, setReviewFilter] = useState("");

  const [enrollmentGlobalFilter, setEnrollmentGlobalFilter] = useState("");
  const [reviewGlobalFilter, setReviewGlobalFilter] = useState("");

  // Fetch all data
  useEffect(() => {
    // Fetch enrollments
    AxiosInstance.get(`companies/enrollments/${batchId}/`)
      .then((response) => {
        setEnrollments(response.data);
      })
      .catch((error) => {
        console.error("Error fetching enrollments:", error);
      });

    // Fetch reviews
    AxiosInstance.get(`companies/reviews/${batchId}/`)
      .then((response) => {
        setReviews(response.data);
      })
      .catch((error) => {
        console.error("Error fetching reviews:", error);
      });
  }, [batchId]);

  // Enrollment columns and table
  const enrollmentColumns = useMemo(
    () => [
      {
        Header: "Trainee Name",
        accessor: (row) => `${row.trainee.first_name} ${row.trainee.last_name}`,
        resizable: true,
      },
      { Header: "Is Enrolled", accessor: "is_enrolled", resizable: true },
      { Header: "Enrolled At", accessor: "enrolled_at", resizable: true },
      {
        Header: "Actions",
        Cell: ({ row }) => (
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={() => handleDeleteEnrollment(row.original.enrollment_id)}
            >
              <FiTrash />
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const {
    getTableProps: getEnrollmentTableProps,
    getTableBodyProps: getEnrollmentTableBodyProps,
    headerGroups: enrollmentHeaderGroups,
    prepareRow: prepareEnrollmentRow,
    page: enrollmentPage,
    canPreviousPage: canPreviousEnrollmentPage,
    canNextPage: canNextEnrollmentPage,
    pageOptions: enrollmentPageOptions,
    nextPage: nextEnrollmentPage,
    previousPage: previousEnrollmentPage,
    state: { pageIndex: enrollmentPageIndex },
    setGlobalFilter: setEnrollmentGlobalFilterFunc,
  } = useTable(
    {
      columns: enrollmentColumns,
      data: enrollments,
      initialState: { pageSize: 8 },
    },
    useGlobalFilter,
    useSortBy,
    useResizeColumns,
    usePagination
  );

  // Review columns and table
  const reviewColumns = useMemo(
    () => [
      {
        Header: "Trainee Name",
        accessor: (row) => `${row.trainee.first_name} ${row.trainee.last_name}`,
        resizable: true,
      },
      { Header: "Review", accessor: "review", resizable: true },
      { Header: "Rating", accessor: "rating", resizable: true },
      {
        Header: "Actions",
        Cell: ({ row }) => (
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={() => handleDeleteReview(row.original.review_id)}
            >
              <FiTrash />
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const {
    getTableProps: getReviewTableProps,
    getTableBodyProps: getReviewTableBodyProps,
    headerGroups: reviewHeaderGroups,
    prepareRow: prepareReviewRow,
    page: reviewPage,
    canPreviousPage: canPreviousReviewPage,
    canNextPage: canNextReviewPage,
    pageOptions: reviewPageOptions,
    nextPage: nextReviewPage,
    previousPage: previousReviewPage,
    state: { pageIndex: reviewPageIndex },
    setGlobalFilter: setReviewGlobalFilterFunc,
  } = useTable(
    {
      columns: reviewColumns,
      data: reviews,
      initialState: { pageSize: 8 },
    },
    useGlobalFilter,
    useSortBy,
    useResizeColumns,
    usePagination
  );

  useEffect(() => {
    setEnrollmentGlobalFilterFunc(enrollmentFilter);
  }, [enrollmentFilter, setEnrollmentGlobalFilterFunc]);

  useEffect(() => {
    setReviewGlobalFilterFunc(reviewFilter);
  }, [reviewFilter, setReviewGlobalFilterFunc]);

  const handleDeleteEnrollment = (enrollmentId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this enrollment?"
    );
    if (!confirmDelete) return;

    AxiosInstance.delete(`companies/enrollment/${enrollmentId}/`)
      .then(() => AxiosInstance.get(`companies/enrollments/${batchId}/`))
      .then((response) => setEnrollments(response.data))
      .catch((error) => console.error("Error deleting enrollment:", error));
  };

  const handleDeleteReview = (reviewId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this review?"
    );
    if (!confirmDelete) return;

    AxiosInstance.delete(`companies/review/${reviewId}/`)
      .then(() => AxiosInstance.get(`companies/reviews/${batchId}/`))
      .then((response) => setReviews(response.data))
      .catch((error) => console.error("Error deleting review:", error));
  };

  return (
    <div className="CompanyPage als-employer-page">
      <UserHeader />
      <div className="company-container py-4">
        <div className="mb-3">
          <button
            className="navigate-button btn btn-outline-primary d-flex align-items-center"
            onClick={() => navigate(-1)}
            style={{ gap: "6px" }}
          >
            <FiArrowLeft /> Back
          </button>
        </div>
        {/* Enrollment Section */}
        <h2 className="section-heading">Enrollments:</h2>
        <div className="card-custom">
          <div className="d-flex justify-content-between mb-3">
            <input
              type="text"
              className="form-control w-50"
              value={enrollmentFilter}
              onChange={(e) => {
                setEnrollmentFilter(e.target.value);
                setEnrollmentGlobalFilter(e.target.value);
              }}
              placeholder="Search enrollments..."
            />
          </div>

          <div className="table-responsive">
            <table
              {...getEnrollmentTableProps()}
              className="table table-bordered table-striped"
            >
              <thead className="table-light">
                {enrollmentHeaderGroups.map((headerGroup) => (
                  <tr
                    {...headerGroup.getHeaderGroupProps()}
                    key={headerGroup.id}
                  >
                    {headerGroup.headers.map((column) => (
                      <th
                        {...column.getHeaderProps(
                          column.getSortByToggleProps()
                        )}
                        key={column.id}
                      >
                        <div {...column.getHeaderProps()}>
                          {column.render("Header")}
                          {column.isSorted
                            ? column.isSortedDesc
                              ? " ▼"
                              : " ▲"
                            : ""}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getEnrollmentTableBodyProps()}>
                {enrollmentPage.length === 0 ? (
                  <tr>
                    <td colSpan={enrollmentColumns.length}>
                      No enrollments found.
                    </td>
                  </tr>
                ) : (
                  enrollmentPage.map((row) => {
                    prepareEnrollmentRow(row);
                    return (
                      <tr {...row.getRowProps()} key={row.id}>
                        {row.cells.map((cell) => (
                          <td {...cell.getCellProps()} key={cell.column.id}>
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

            <div className="pagination d-flex justify-content-center align-items-center gap-3 mt-3">
              <button
                onClick={() => previousEnrollmentPage()}
                disabled={!canPreviousEnrollmentPage}
                className="btn btn-outline-primary"
              >
                Previous
              </button>
              <span>
                Page{" "}
                <strong>
                  {enrollmentPageIndex + 1} of {enrollmentPageOptions.length}
                </strong>{" "}
              </span>
              <button
                onClick={() => nextEnrollmentPage()}
                disabled={!canNextEnrollmentPage}
                className="btn btn-outline-primary"
              >
                Next
              </button>
            </div>
        </div>
        {/* Reviews & Ratings section */}
        <h2 className="section-heading">Reviews & Ratings:</h2>
        <div className="card-custom">
          <div className="d-flex justify-content-between mb-3">
            <input
              type="text"
              className="form-control w-50"
              value={reviewFilter}
              onChange={(e) => {
                setReviewFilter(e.target.value);
                setReviewGlobalFilter(e.target.value);
              }}
              placeholder="Search reviews..."
            />
          </div>

          <div className="table-responsive">
            <table
              {...getReviewTableProps()}
              className="table table-bordered table-striped"
            >
              <thead>
                {reviewHeaderGroups.map((headerGroup) => (
                  <tr
                    {...headerGroup.getHeaderGroupProps()}
                    key={headerGroup.id}
                  >
                    {headerGroup.headers.map((column) => (
                      <th
                        {...column.getHeaderProps(
                          column.getSortByToggleProps()
                        )}
                        key={column.id}
                      >
                        <div {...column.getHeaderProps()}>
                          {column.render("Header")}
                          {column.isSorted
                            ? column.isSortedDesc
                              ? " ▼"
                              : " ▲"
                            : ""}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getReviewTableBodyProps()}>
                {reviewPage.length === 0 ? (
                  <tr>
                    <td colSpan={5}>No reviews found.</td>
                  </tr>
                ) : (
                  reviewPage.map((row) => {
                    prepareReviewRow(row);
                    return (
                      <tr {...row.getRowProps()} key={row.id}>
                        {row.cells.map((cell) => (
                          <td {...cell.getCellProps()} key={cell.column.id}>
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

            <div className="pagination d-flex justify-content-center align-items-center gap-3 mt-3">
              <button
                onClick={() => previousReviewPage()}
                disabled={!canPreviousReviewPage}
                className="btn btn-outline-primary"
              >
                Previous
              </button>
              <span>
                Page{" "}
                <strong>
                  {reviewPageIndex + 1} of {reviewPageOptions.length}
                </strong>{" "}
              </span>
              <button
                onClick={() => nextReviewPage()}
                disabled={!canNextReviewPage}
                className="btn btn-outline-primary"
              >
                Next
              </button>
            </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
